import React from 'react';
import { MapPin, Truck, Clock, Activity, Phone, ExternalLink, AlertTriangle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { cn } from '../../utils/cn';
import { Site } from '../../utils/multiSiteData';
interface SiteCardProps {
  site: Site;
  onViewSite: (siteId: string) => void;
}
export function SiteCard({
  site,
  onViewSite
}: SiteCardProps) {
  const isCritical = site.status === 'critical';
  const isAttention = site.status === 'attention';
  return <Card className={cn('flex flex-col h-full transition-all duration-300 hover:border-gray-700', isCritical && 'border-red-500/30 bg-red-500/5 shadow-[0_0_15px_rgba(239,68,68,0.1)]', isAttention && 'border-amber-500/30 bg-amber-500/5')} noPadding data-id="element-207">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex items-start justify-between" data-id="element-208">
        <div data-id="element-209">
          <div className="flex items-center gap-2 mb-1" data-id="element-210">
            <h3 className="font-semibold text-white" data-id="element-211">{site.name}</h3>
            <div className={cn('h-2 w-2 rounded-full animate-pulse', isCritical ? 'bg-red-500' : isAttention ? 'bg-amber-500' : 'bg-emerald-500')} data-id="element-212" />
          </div>
          <div className="flex items-center text-xs text-gray-500" data-id="element-213">
            <MapPin className="h-3 w-3 mr-1" data-id="element-214" />
            {site.city}, {site.state}
          </div>
        </div>
        {site.congestionLevel !== 'low' && <Badge variant={site.congestionLevel === 'high' ? 'danger' : 'warning'} data-id="element-215">
            {site.congestionLevel === 'high' ? 'High Congestion' : 'Busy'}
          </Badge>}
      </div>

      {/* Metrics Grid */}
      <div className="p-4 grid grid-cols-2 gap-4 flex-1" data-id="element-216">
        <div className="space-y-1" data-id="element-217">
          <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-218">
            <Truck className="h-3 w-3" data-id="element-219" /> Inside
          </div>
          <div className="text-xl font-bold text-white" data-id="element-220">
            {site.trucksInside}
          </div>
        </div>

        <div className="space-y-1" data-id="element-221">
          <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-222">
            <Clock className="h-3 w-3" data-id="element-223" /> Over SLA
          </div>
          <div className={cn('text-xl font-bold', site.trucksOverSLA > 0 ? 'text-red-500' : 'text-gray-300')} data-id="element-224">
            {site.trucksOverSLA}
          </div>
        </div>

        <div className="space-y-1" data-id="element-225">
          <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-226">
            <Activity className="h-3 w-3" data-id="element-227" /> Approaching
          </div>
          <div className={cn('text-xl font-bold', site.trucksApproachingSLA > 0 ? 'text-amber-500' : 'text-gray-300')} data-id="element-228">
            {site.trucksApproachingSLA}
          </div>
        </div>

        <div className="space-y-1" data-id="element-229">
          <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-230">
            <AlertTriangle className="h-3 w-3" data-id="element-231" /> Alerts
          </div>
          <div className={cn('text-xl font-bold', site.alerts.length > 0 ? 'text-red-500' : 'text-gray-300')} data-id="element-232">
            {site.alerts.length}
          </div>
        </div>
      </div>

      {/* SLA Risk Bar */}
      <div className="px-4 pb-4" data-id="element-233">
        <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-234">
          <span data-id="element-235">SLA Risk Level</span>
          <span data-id="element-236">
            {Math.round((site.trucksOverSLA + site.trucksApproachingSLA) / site.trucksInside * 100)}
            %
          </span>
        </div>
        <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden" data-id="element-237">
          <div className={cn('h-full rounded-full transition-all duration-500', isCritical ? 'bg-red-500' : isAttention ? 'bg-amber-500' : 'bg-emerald-500')} style={{
          width: `${Math.min(100, (site.trucksOverSLA * 2 + site.trucksApproachingSLA) / site.trucksInside * 100)}%`
        }} data-id="element-238" />
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-3 bg-gray-900/50 border-t border-gray-800 flex gap-2" data-id="element-239">
        <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => onViewSite(site.id)} data-id="element-240">
          <ExternalLink className="h-3 w-3 mr-1.5" data-id="element-241" />
          View Site
        </Button>
        <Button variant="ghost" size="sm" className="px-2 h-8 text-gray-400 hover:text-white" data-id="element-242">
          <Phone className="h-3 w-3" data-id="element-243" />
        </Button>
      </div>
    </Card>;
}